/**
 * dexSyncOnSignIn — pull the server dex the moment a Pro trainer is signed in,
 * not the first time they happen to open the Dex page.
 *
 * Reported on the owner's Galaxy (2026-09-16, fresh install of 1.9.0): after
 * Google sign-in the Profile read Main 0 / Shiny 0 / Lucky 0 / Perfect 0 for
 * an account with a 7,000-row backup. The only pull ran in Pokedex.tsx's
 * mount effect, so a reinstall landed on a Profile that said the dex was
 * gone until the trainer tapped View Dex. Same sequence the Dex page runs
 * (backfill, then pull, then the totals checkpoint), once per sign-in per
 * app run; the Dex page keeps its own per-mount pull for other-device
 * changes.
 *
 * Basic is untouched: the pull is a Pro cross-device feature, and the Dex
 * page's restore-with-Pro probe stays where it is.
 */
import {
  bindDexSyncApiFetch,
  commitDexTotals,
  ensureDexBackfillFromRecords,
  pullDexStateFromServer,
} from "./dexStateStore";

type ApiFetch = (method: string, url: string, data?: unknown) => Promise<Response>;

let lastSyncedToken: string | null = null;

/** Test hook: forget which token was synced. */
export function __resetDexSyncOnSignInForTest(): void {
  lastSyncedToken = null;
}

/**
 * Run the sign-in pull once for `token`. Returns true when it ran.
 * Safe to call on every render of the effect that owns it: a repeat call
 * for the same token, a missing token, or a non-Pro tier is a no-op.
 */
export async function syncDexOnSignIn(args: {
  token: string | null;
  isPro: boolean;
  apiFetch: ApiFetch;
}): Promise<boolean> {
  const { token, isPro, apiFetch } = args;
  if (!token || !isPro) return false;
  if (lastSyncedToken === token) return false;
  lastSyncedToken = token;
  bindDexSyncApiFetch(apiFetch);
  try {
    await ensureDexBackfillFromRecords(apiFetch); // must run before pull
    await pullDexStateFromServer(apiFetch);
  } catch {
    // Best effort: the Dex page pulls again on its own mount.
  } finally {
    commitDexTotals();
  }
  return true;
}
